'use client';

import {
  Button,
  Card,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@routly/ui';
import { Plus, Save } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ErrorState, LoadingState, PageHeader } from '@/components/shared';
import { useSaveSensorMapping, useSensorMappings } from '@/hooks/useSensorMappings';
import type { SensorDefinition, SensorDefinitions } from '@/lib/types';
import { MappingPreview } from './MappingPreview';
import { SensorRow } from './SensorRow';

const TYPES: SensorDefinition['type'][] = ['measurement', 'digital', 'state', 'virtual'];

/** A new sensor of each type, with the fields that type needs already present. */
const BLANK: Record<SensorDefinition['type'], SensorDefinition> = {
  measurement: { type: 'measurement', sources: [], scale: 1, offset: 0 },
  digital: { type: 'digital', sources: [], invert: false },
  state: { type: 'state', sources: [], states: {} },
  virtual: { type: 'virtual', expr: '' },
};

/**
 * What a tracker family's parameters are called.
 *
 * A device reports `io_239`; a calculator wants `$pto`. This is where the two
 * are connected, per tracker family, because that is what decides the parameter
 * names — two vehicles carrying the same Teltonika model report identically
 * whoever owns them.
 *
 * Edits here are the organisation's rung. The system rung underneath is what
 * every fleet inherits, and a sensor shown as inherited is coming from it.
 */
export function SensorMappingsPage() {
  const { data, isLoading, error } = useSensorMappings();
  const save = useSaveSensorMapping();

  const [family, setFamily] = useState('teltonika');
  const [draft, setDraft] = useState<SensorDefinitions | null>(null);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<SensorDefinition['type']>('digital');

  const mapping = data?.data.find((row) => row.trackerType === family);

  const inherited = useMemo(() => mapping?.system?.definitions ?? {}, [mapping]);
  // The draft only exists once somebody edits. Until then the saved document is
  // shown directly, so switching families does not carry the last one's edits.
  const definitions = useMemo(
    () => draft ?? mapping?.organization?.definitions ?? {},
    [draft, mapping]
  );

  const merged = useMemo(() => ({ ...inherited, ...definitions }), [inherited, definitions]);

  if (isLoading) return <LoadingState />;
  if (error || !data) return <ErrorState title="Could not load sensor mappings" />;

  const update = (next: SensorDefinitions) => setDraft(next);

  const addSensor = () => {
    const name = newName.trim();
    if (!name) return;
    update({ ...definitions, [name]: BLANK[newType] });
    setNewName('');
  };

  const handleSave = async () => {
    try {
      await save.mutateAsync({ trackerType: family, definitions, system: false });
      setDraft(null);
      toast.success('Mapping saved', { description: 'The next message will use it.' });
    } catch (saveError) {
      toast.error('Could not save', {
        description: saveError instanceof Error ? saveError.message : 'Something went wrong',
      });
    }
  };

  return (
    <>
      <PageHeader
        title="Sensor mappings"
        description="What each tracker family's parameters are called, so a calculator can say $pto instead of io_239."
      />

      <div className="flex items-center gap-3">
        <Select
          value={family}
          onValueChange={(value) => {
            setFamily(value);
            setDraft(null);
          }}
        >
          <SelectTrigger className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {data.data.map((row) => (
              <SelectItem key={row.trackerType} value={row.trackerType}>
                {row.trackerType}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button onClick={handleSave} disabled={draft === null || save.isPending}>
          <Save className="mr-2 h-4 w-4" />
          Save
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Sensor</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Reads</TableHead>
              <TableHead>Details</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(merged).map(([name, definition]) => (
              <SensorRow
                key={name}
                name={name}
                definition={definition}
                inherited={!(name in definitions)}
                onChange={(next) => update({ ...definitions, [name]: next })}
                onRemove={() => {
                  const next = { ...definitions };
                  delete next[name];
                  update(next);
                }}
              />
            ))}
          </TableBody>
        </Table>

        <div className="flex items-center gap-2 border-t border-border p-4">
          <Input
            aria-label="New sensor name"
            className="w-48"
            placeholder="doorFront"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
          />
          <Select value={newType} onValueChange={(value) => setNewType(value as typeof newType)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={addSensor}>
            <Plus className="mr-2 h-4 w-4" />
            Add sensor
          </Button>
        </div>
      </Card>

      <MappingPreview definitions={merged} />
    </>
  );
}
